import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding, inject,
  Input,
} from "@angular/core";
import { IShape } from '../../core/interfaces/IShape';
import { ShapesService } from '../../core/services/ShapesService';
import { FlattenSeat } from "../../core/types";
import { SvgToBase64BgPipe } from '../../core/pipes/svg-to-base64-bg.pipe';
import { ElementTypes } from '../../core/enums/ElementTypes';
import { SeatStatuses } from '../../core/enums/SeatStatuses';
import { ShapeSeatTypes } from '../../core/enums/ShapeSeatTypes';
import { ContrastTextDirective } from '../../core/directives/contrast-text.directive';
import { BaseComponent } from '../../../../core/layout/components/abstracts/base.component';
import { LongPressDirective } from '../../../../shared/directives/long-press.directive';
import { SeatsPlanService } from '../../core/services/SeatsPlanService';
import { ShapeSeatTypeUtils } from '../../core/utils/ShapeSeatTypeUtils';

@Component({
  selector: 'app-shape',
  templateUrl: './shape.component.html',
  styleUrls: ['./shape.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'shape'
  },
  imports: [
    CommonModule,
    SvgToBase64BgPipe,
    ContrastTextDirective,
    LongPressDirective,
  ],
  providers: [],
})
export class ShapeComponent extends BaseComponent {

  @Input() isLegendShape = false;

  @Input() set shape(value: IShape) {
    if (value) {
      this._shape = value;

      if (this._shape.id) {
        this.id = this._shape.id;
      }

      if (this._shape?.shapeType) {
        this._shapesService.setShapeType(this._elementRef.nativeElement, this._shape.shapeType);
      }
    }
  };

  @Input() set dataObject(value: FlattenSeat) {
    this._dataObject = value;

    if (value.x !== undefined && value.x !== null) {
      this.x = `${value.x}px`;
    }

    if (value.y !== undefined && value.y !== null) {
      this.y = `${value.y}px`;
    }

    if ((value.width !== undefined && value.width !== null)) {
      this.width = `${value.width}px`;
    }

    if ((value.height !== undefined && value.height !== null)) {
      this.height = `${value.height}px`;
    }

    if (value.id) {
      this.dataObjectId = value.id;
    }

    if (value.seatId) {
      this.seatId = value.seatId;
    }

    this._cdr.detectChanges();
  }

  @HostBinding('style.left') @Input() x: string;
  @HostBinding('style.top') @Input() y: string;
  @HostBinding('style.width') @Input() width: string;
  @HostBinding('style.height') @Input() height: string;
  @HostBinding('attr.id') @Input() id: number;
  @HostBinding('attr.data-object-id') @Input() dataObjectId: number;
  @HostBinding('attr.data-seat-id') @Input() seatId: number;
  @HostBinding('class.pointer-events-none')
  public get isNonInteractiveVisual(): boolean {
    return !this.isLegendShape && ShapeSeatTypeUtils.isVisualShape(this._shape);
  }
  @HostBinding('class.shape--staircase')
  public get isStaircase(): boolean {
    return ShapeSeatTypeUtils.resolveElementType(this._shape?.shapeElementTypeKey) === ElementTypes.STAIRCASE;
  }

  public readonly seatStatuses = SeatStatuses;
  public readonly shapeSeatTypes = ShapeSeatTypes;
  public readonly shapeSeatTypeUtils = ShapeSeatTypeUtils;

  private _shape: IShape;
  private _dataObject: FlattenSeat;
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _shapesService = inject(ShapesService);
  private readonly _seatsPlanService = inject(SeatsPlanService);

  public get dataObject(): FlattenSeat {
    return this._dataObject;
  }

  public get shape(): IShape {
    return this._shape;
  }

  public onSeatShortPress(): void {
    if (this.isLegendShape || !ShapeSeatTypeUtils.isBookableShape(this._shape)) {
      return;
    }

    const seat = this._seatsPlanService.seats().find(s => s.seatId === this.seatId);
    if (!seat) {
      return;
    }

    this._seatsPlanService.seatShortTouch$.next(seat);
  }

  public onSeatLongPress(): void {
    if (this.isLegendShape || !ShapeSeatTypeUtils.isBookableShape(this._shape)) {
      return;
    }

    const seat = this._seatsPlanService.seats().find(s => s.seatId === this.seatId);
    if (!seat) {
      return;
    }

    if (this.isCustomerScreen) {
      return;
    }

    if (seat.seatStatusKey !== SeatStatuses.FREE && seat.seatStatusKey !== SeatStatuses.BLOCKED) {
      return;
    }

    this._seatsPlanService.seatBlockLongPress$.next(seat);
  }
}
